<template>
  <v-dialog
    v-model="showDialog"
    persistent
    :max-width="mobile ? '' : '1200px'"
    :fullscreen="mobile"
    scrollable
  >
    <v-card class="pa-2" v-if="showDialog" data-testid="report-dialog">
      <v-card-title style="max-height:75px">
        <div class="d-flex align-center">
          <div class="md-subheader-no-spacing overflow-ellipsis">
            {{ reportName }}
          </div>
          <v-spacer />
          <v-btn @click="close" icon variant="plain"><v-icon icon="close" /></v-btn>
        </div>
      </v-card-title>
      <v-divider />
      <v-card-text class="mobile-card-text">
        <div
          v-if="!report || report.length == 0"
          class="pa-3 md-light"
          style="height: 100px"
        >
          This report is empty. Your store has no relevant information for this
          report.
        </div>
        <template v-else>
        <div
          v-for="(table, idx) in report"
          :key="table.name + idx"
          class="report-container"
        >
          <v-divider />
          <div v-if="!hideTableHeaders" class="section-header pa-2">
            <div class="field-header pa-2" v-if="table.header">
              <div>{{ table.header }}</div>
            </div>
            <div class="field-header pa-2">
              {{ table.name }}
            </div>
            <div class="field-header pl-2 pr-2" v-if="table.tableDesc">
              {{ table.tableDesc }}
            </div>
          </div>
          <v-divider />

          <v-row
            align="center"
            justify="start"
            style="padding: 16px"
            v-if="table.metadata && Object.keys(table.metadata).length > 0"
          >
            <v-col
              cols="12"
              sm="6"
              :md="name !== 'Decorators' ? 2 : 6"
              v-for="(value, name) in table.metadata"
              :key="name"
              class="pb-3 pt-3 pr-1"
            >
              <div class="field-header">{{ name }}</div>
              <div
                v-if="Array.isArray(value)"
                class="field-value overflow-ellipsis"
              >
                <div v-for="(item, index) in value" :key="index">
                  {{ item }}
                </div>
              </div>
              <div v-else class="field-value overflow-ellipsis">
                <span>{{ value }}</span>
              </div>
            </v-col>
          </v-row>
          <v-table>
            <thead v-if="mobile && reportName != 'Orders Itemized'">
              <tr>
                <th :colspan="table.columns.length" style="padding: 0;">
                  <responsive-table-search
                    :headers="table.columns.map((c) => ({ text: c, value: c }))"
                    :pagination="getTablePagination(idx)"
                    flat
                    hideSearch
                  >
                  </responsive-table-search>
                </th>
              </tr>
            </thead>
            <thead v-else-if="mobile && reportName == 'Orders Itemized'">
              <tr style="height: 0px">
                <th></th>
              </tr>
            </thead>
            <thead v-else>
              <tr>
                <th
                  v-for="(column, colIdx) in table.columns"
                  :key="colIdx"
                  :class="[
                    'column sortable',
                    getTablePagination(idx).descending ? 'desc' : 'asc',
                    column === getTablePagination(idx).sortBy ? 'active' : '',
                  ]"
                  @click="sortReportColumn(idx, column)"
                  style="cursor: pointer"
                >
                  <div class="d-flex align-center">
                    {{ column }}
                    <v-icon
                      size="small"
                      class="ml-1"
                      :icon="getReportSortIcon(idx, column)"
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, rowIdx) in getSortedRows(table, idx)" :key="rowIdx">
                <template v-if="mobile">
                  <td :colspan="table.columns.length" style="padding: 0;">
                    <responsive-table-item
                      :headers="table.columns.map((c) => ({ text: c, value: c }))"
                      :item="item"
                      infoType="report"
                      flat
                    />
                  </td>
                </template>
                <template v-else>
                  <td v-for="(column, colIdx) in table.columns" :key="colIdx">
                    {{ item[column] }}
                  </td>
                </template>
              </tr>
            </tbody>
          </v-table>
        </div>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import ResponsiveTableItem from "./ResponsiveTableItem.vue";
import ResponsiveTableSearch from "./ResponsiveTableSearch.vue";

export default {
  components: { ResponsiveTableItem, ResponsiveTableSearch },
  props: {
    campaign: {
      type: Object,
      required: true,
    },

    report: {
      type: Array,
      required: true,
    },

    reportName: {
      type: String,
      required: true,
    },

    selectedReportInfo: {
      type: Object,
      required: true,
    },

    hideTableHeaders: {
      type: Boolean,
      required: false,
      default: false,
    },
    selectedBatchId: {
      type: String,
      default: null,
    },
  },

  watch: {
    pagination: {
      handler: function(val, oldVal) {
        var vm = this;
        val.filter(function(p, idx) {
          return Object.keys(p).some(function(prop) {
            if (vm.clonePagination && vm.clonePagination[idx]) {
              var diff = p[prop] !== vm.clonePagination[idx][prop];
              if (diff) {
                vm.clonePagination[idx] = p;
              }
            }
          });
        });
      },
      deep: true,
    },
    report: {
      handler(tables) {
        if (tables && tables.length > 0) {
          const updated = [...this.pagination];
          tables.forEach((table, idx) => {
            if (!updated[idx]) {
              updated[idx] = { sortBy: null, descending: false, page: 1, rowsPerPage: 50 };
            }
          });
          this.pagination = updated;
          this.clonePagination = this.pagination.map((a) => Object.assign({}, a));
        }
      },
      immediate: true,
    },
  },

  computed: {
    ...mapGetters(["getPdfBlacklistReport"]),
  },

  data() {
    return {
      pagination: [],
      clonePagination: [],
      availableReports: [],
      reportsPending: false,
      showDialog: false,
      mobile: window.innerWidth < 980,
    };
  },

  async mounted() {
    this.clonePagination = this.pagination.map((a) => Object.assign({}, a));
    this.loadComponent();
  },

  created() {
    window.addEventListener("resize", this.handleWindowResize);
  },
  unmounted() {
    window.removeEventListener("resize", this.handleWindowResize);
  },

  methods: {
    handleWindowResize(e) {
      let width = window.innerWidth;
      if (width < 980) {
        this.mobile = true;
      } else {
        this.mobile = false;
      }
    },
    loadComponent() {
      if (this.campaign && this.campaign._jv && this.campaign._jv.id)
        this.loadReports(this.campaign._jv.id);
    },

    async loadReports(campaign_id) {
      // load available reports
      this.reportsPending = true;
      try {
        let reports = await this.$axios.get(
          `/reports/${campaign_id}/${this.selectedBatchId}`
        );
        this.availableReports = reports.data;
      } finally {
        this.reportsPending = false;
      }
    },

    getTablePagination(idx) {
      return this.pagination[idx] || { sortBy: null, descending: false, page: 1, rowsPerPage: 50 };
    },

    sortReportColumn(idx, column) {
      const pag = this.getTablePagination(idx);
      if (pag.sortBy === column) {
        pag.descending = !pag.descending;
      } else {
        pag.sortBy = column;
        pag.descending = false;
      }
      this.pagination = [...this.pagination];
    },
    getReportSortIcon(idx, column) {
      if (this.pagination[idx] && this.pagination[idx].sortBy === column) {
        return this.pagination[idx].descending ? 'arrow_downward' : 'arrow_upward';
      }
      return 'arrow_upward';
    },

    getSortedRows(table, idx) {
      const pag = this.getTablePagination(idx);
      if (!pag.sortBy) {
        return table.rows;
      }
      // Separate total/summary rows from data rows before sorting.
      // Total rows are pinned at the bottom regardless of sort order.
      const dataRows = table.rows.filter(r => !this.isTotalRow(r));
      const totalRows = table.rows.filter(r => this.isTotalRow(r));
      const sorted = this.customSort([...dataRows], pag.sortBy, pag.descending);
      return [...sorted, ...totalRows];
    },

    isTotalRow(row) {
      return Object.values(row).some(
        (val) => typeof val === 'string' && /\bTotals?\b/i.test(val)
      );
    },

    customSort(items, index, isDescending) {
      // do not apply sorting to the Itemized Shopper report
      if (this.$props.reportName === "Orders Itemized") {
        return items;
      }

      items.sort((a, b) => {
        let tmpa = Object.assign({}, a);
        let tmpb = Object.assign({}, b);

        if (index === "Bin Number") {
          tmpa[index] = parseInt(a[index].substr(4));
          tmpb[index] = parseInt(b[index].substr(4));
        }

        if (isDescending) {
          return tmpa[index] > tmpb[index] ? -1 : 1;
        } else {
          return tmpa[index] < tmpb[index] ? -1 : 1;
        }
      });

      return items;
    },

    downloadReport(reportFormat) {
      this.$emit(
        "submit",
        this.campaign,
        this.availableReports.find((report) => report.name === this.reportName),
        this.availableReports,
        reportFormat
      );
    },

    show() {
      this.showDialog = true;
    },

    close() {
      this.showDialog = false;
    },
  },
};
</script>
<style scoped>
.report-container {
  width: 100%;
}

.back-button {
  position: fixed !important;
  top: 170px !important;
  left: 25px !important;
  z-index: 2 !important;
}
.table-title {
  margin-bottom: 24px;
}

.section-header {
  background: #faf5f5;
  padding-left: 16px;
}

.field-header {
  color: var(--tf-fg-2);
  font-weight: 500;
  font-size: 14px;
}

.field-value {
  color: var(--tf-fg-3);
  font-weight: normal;
  font-size: 16px;
}

.mobile-card-text {
  padding: 0px;
}

.info-row {
  padding-left: 16px;
  padding-top: 16px;
  padding-bottom: 16px;
}

.order-item {
  padding-top: 16px;
  padding-bottom: 16px;
}
</style>

